<template>
  <v-dialog
    id="contact_your_rep_modal"
    :model-value="modelValue"
    :max-width="submitted === true ? '560px' : '914px'"
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.xs ? '100vw' : '914px'"
    :min-width="$vuetify.display.xs ? '100vw' : '914px'"
    :min-height="$vuetify.display.xs ? '92vh' : 'fit-content'"
    scrollable
    content-class="contact-rep-dialog-content"
    z-index="10000"
    @update:modelValue="(v: any) => v || $emit('close')"
  >
    <!-- Submitted -->
    <v-card v-if="submitted" :class="['contact-rep-card', modalClass]">
      <v-card-title class="pb-0">
        <p class="text-right">
          <a
            :class="closeSelector"
            href="javascript:void(0)"
            role="button"
            @click="close()"
            aria-label="close"
          >
            <v-icon :icon="mdiClose" size="x-large" />
          </a>
        </p>
      </v-card-title>
      <v-card-text class="pt-0">
        <!-- Your message has been sent! -->
        <h2 class="text-h2 text-center mb-2" aria-live="assertive">
          {{ $t('L_your_message_has_been_sent') }}
        </h2>
        <!-- Your Representative will respond -->
        <h4 class="text-h4 font-weight-regular text-center mb-4">
          {{ $t('L_your_representative_will_respond_to_your_message_using') }}
        </h4>
        <OnexButton
          role="link"
          color="primary"
          class="maxSizeForCloseButton OkButton d-flex align-center"
          @click="close()"
          aria-label="close"
        >
          {{ $t('L_ok') }}
        </OnexButton>
      </v-card-text>
    </v-card>
    <!-- Not Submitted -->
    <v-card v-else :class="['contact-rep-card', modalClass]">
      <OnexAlert
        v-if="err"
        :type="Notifications.error"
        dismissible="true"
        @click:close="err = false"
      >
        {{ $t('L_contact_send_error') }}
      </OnexAlert>
      <!-- Currently the success alert is not used -->
      <OnexAlert
        v-if="success"
        class="mb-4"
        :type="Notifications.success"
        dismissible="true"
        @click:close="close"
      >
        {{ $t('L_contact_send_success') }}
      </OnexAlert>
      <!-- close icon -->
      <v-card-title class="pb-0">
        <p class="text-right mb-0">
          <a
            :class="closeSelector"
            href="javascript:void(0)"
            role="button"
            @click="close()"
            aria-label="close"
          >
            <v-icon :icon="mdiClose" />
          </a>
        </p>
      </v-card-title>
      <!-- Contact Your Rep -->
      <v-card-text class="pt-0">
        <h2 class="heading_two text-center" aria-live="assertive">
          {{ $t('L_contact_contact_your_rep') }}
        </h2>
      </v-card-text>

      <!-- Agent selection -->
      <v-row v-if="agents.length > 1" justify="center">
        <v-col cols="10" md="8" lg="6">
          <div class="font-weight-medium">
            {{ $t('L_contact_select_agent') }}
          </div>
          <div>
            <OnexSelect
              :label="$t('')"
              :items="agents"
              item-title="name"
              item-value="agentId"
              v-model="currentAgent"
              color="black"
              :menu-props="{ location: 'bottom center', scrollStrategy: 'close' }"
            />
          </div>
        </v-col>
      </v-row>
      <!-- Contact Form -->
      <v-form id="phone-form" ref="phone-form" v-model="validForm" autocomplete="off">
        <v-row justify="center">
          <v-col cols="10" md="8" lg="6">
            <v-row>
              <!-- I would like to -->
              <v-col cols="12" class="mt-5">
                <ReasonInput
                  :form-input="v$.reason"
                  :items="options"
                  v-model="v$.reason.$model"
                  id-prop="contact_your_rep_options"
                />
              </v-col>
              <!-- Subject -->
              <v-col v-if="v$.reason.$model === 'other'" cols="12">
                <SubjectInput v-model="v$.subject.$model" :form-input="v$.subject" />
              </v-col>
              <!-- Message -->
              <v-col cols="12">
                <MessageInput v-model="v$.message.$model" :form-input="v$.message" />
              </v-col>
              <!-- How would you prefer to be contacted? -->
              <v-col cols="12" class="text-center">
                <p class="mb-2">{{ $t('L_how_would_you_prefer_to_be_contacted') }}</p>
                <div
                  class="buttonCommonCss"
                  :class="contactOption === 1 ? 'blueButtonWhiteText' : 'whiteButtonBlueText'"
                  @click="contactOption = 1"
                >
                  {{ $t('L_contact_email') }}
                </div>
                <div
                  class="buttonCommonCss"
                  :class="contactOption === 2 ? 'blueButtonWhiteText' : 'whiteButtonBlueText'"
                  @click="contactOption = 2"
                >
                  {{ $t('L_contact_telephone') }}
                </div>
              </v-col>
              <!-- Email Input -->
              <v-col cols="12" v-if="contactOption === 1">
                <OnexEmail
                  class="contact-black"
                  v-model="v$.email.$model"
                  :form-input="v$.email"
                  :label="$t('L_contact_email')"
                />
              </v-col>
              <!-- Phone Input -->
              <v-col cols="12" v-if="contactOption === 2">
                <OnexPhone
                  class="contact-black"
                  v-model="telephone"
                  :form-input="v$.telephone"
                  :label="$t('L_contact_telephone')"
                />
              </v-col>
              <v-col cols="12" class="d-flex flex-column">
                <OnexButton
                  color="primary"
                  :class="sendButtonClass"
                  :disabled="sendButtonDisable || !validForm"
                  @click="send()"
                >
                  {{ $t('L_contact_send') }}
                </OnexButton>
                <OnexButton
                  variant="outlined"
                  primary
                  class="mt-3 mb-16"
                  @click="close()"
                  :class="closeSelector"
                  aria-label="close"
                >
                  {{ $t('L_contact_clear') }}
                </OnexButton>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { required, requiredIf, maxLength } from '@vuelidate/validators';
import { ReasonInput, SubjectInput, MessageInput } from '../../components/Inputs';
import { titleCase, phoneNumber } from '../../shared/util/formatters';
import { defineComponent, type ComponentOptions } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { mdiCheckCircleOutline, mdiClose } from '@mdi/js';
import {
  OnexButton,
  OnexSelect,
  OnexEmail,
  OnexPhone,
  OnexAlert,
  Notifications,
} from '@primerica/onex-ui-lib';
import type { IGlobalStoreAgent } from '@/stores/global/globalStore.model.ts';
import { mapState } from 'pinia';
import { useCustomerStore } from '@/stores/customer/customer.store.ts';

export const requiredIfOther = ((vm: { v$: { reason: { $model: string } } }) =>
  vm.v$.reason.$model === 'other') as () => boolean;

const componentOptions: ComponentOptions = {
  name: 'ContactRep',
  components: {
    ReasonInput,
    SubjectInput,
    MessageInput,
    OnexButton,
    OnexSelect,
    OnexEmail,
    OnexPhone,
    OnexAlert,
  },
  data: () => ({
    submitted: false,
    validForm: false,
    telephone: '',
    email: '',
    message: '',
    subject: '',
    messageMaxLength: 500,
    reason: '',
    err: false,
    success: false,
    agent: {} as IGlobalStoreAgent,
    currentAgent: '',
    contactOption: '' as string | number,
    v$: useVuelidate(),
    mdiCheckCircleOutline,
    mdiClose,
    Notifications,
  }),
  validations: {
    reason: {
      required,
    },
    message: {},
    contactOption: {
      required,
    },
    email: {
      required,
    },
    subject: {
      required: requiredIf(requiredIfOther),
      maxlength: maxLength(60),
    },
  },
  props: {
    agents: {
      type: Array,
      required: false,
      default: () => [],
    },
    modelValue: {
      type: Boolean,
      required: true,
      default: false,
    },
    closeSelector: {
      type: String,
      required: false,
      default: 'modal_close',
    },
    modalClass: {
      type: String,
      required: false,
      default: 'modal_close',
    },
    sender: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  computed: {
    ...mapState(useCustomerStore, ['sendRepEmailError']),
    sendButtonDisable() {
      let disable = false;
      if (this.reason === null || this.reason.length === 0) {
        disable = true;
      }
      if (this.contactOption === '') {
        disable = true;
      }
      if (this.v$.email.$invalid && this.contactOption === 1) {
        disable = true;
      }
      if (this.contactOption === 2 && (this.reason.length === 0 || this.telephone.length < 10)) {
        disable = true;
      }
      return disable;
    },
    agentName() {
      return this.agent?.name ? titleCase(this.agent.name) : '';
    },
    agentEmail() {
      return this.agent?.email?.length > 0 ? this.agent.email.toLowerCase() : '';
    },
    agentPhone() {
      return phoneNumber(this.agent?.office?.phone);
    },
    agentHasContactInfo() {
      return this.agent?.email?.length > 0 || this.agentPhone?.length > 0;
    },
    repImage() {
      return this.agent?.displayPic;
    },
    sendButtonClass() {
      if (this.v$.reason.$model === 'question') {
        return 'email_rep_about_policy';
      }
      if (this.v$.reason.$model) {
        return `email_rep_about_${this.v$.reason.$model}`;
      }
      return 'email_rep_about_option';
    },
    options() {
      let investments = [];
      let lifeInsurance = [];
      if (this.agent?.licensedProducts) {
        lifeInsurance = this.agent?.licensedProducts.find(
          (product: never) => product === 'Term Life Insurance'
        );
        investments = this.agent?.licensedProducts.find(
          (product: string) => product.includes('Annuities') || product === 'Securities'
        );
      }
      const options = [];
      if (lifeInsurance) {
        options.push(this.$t('L_contact_select_question'));
        options.push(this.$t('L_contact_select_coverage'));
      }
      if (investments) {
        options.push(this.$t('L_contact_select_investing'));
      }
      options.push(this.$t('L_contact_select_other'));
      return options;
    },
  },
  methods: {
    close() {
      this.success = false;
      this.err = false;
      this.$emit('close');
      this.v$.message.$model = '';
      this.v$.subject.$model = '';
      this.v$.reason.$model = '';
      this.contactOption = '';
      this.v$.$reset();
      this.submitted = false;
    },
    clearErr() {
      this.err = false;
    },
    send() {
      this.submitted = true;
      const subject = this.v$.reason.$model;
      if (this.contactOption === 1) {
        this.$emit('send-message', {
          agentId: this.agent?.agentId,
          sender: {
            emailAddress: this.v$.email.$model,
            firstName: this.sender.firstName,
            lastName: this.sender.lastName,
            phoneNumber: this.sender.phone,
            preferredContact: 'EMAIL',
          },
          reason: subject,
          message: this.v$.message.$model,
        });
      } else if (this.contactOption === 2) {
        this.$emit('send-message', {
          agentId: this.agent?.agentId,
          sender: {
            emailAddress: this.v$.email.$model,
            firstName: this.sender.firstName,
            lastName: this.sender.lastName,
            phoneNumber: this.sender.phone,
            preferredContact: 'PHONE',
          },
          reason: subject,
          message: this.v$.message.$model,
        });
      }
    },
  },
  created() {
    this.submitted = false;
    if (this.agents) {
      [this.agent] = this.agents;
      this.currentAgent = this.agent?.agentId;
    }
    if (this.sender) {
      this.telephone = this.sender.phone + '';
      this.email = this.sender.email;
    }
    this.contactOption = '';
  },
  watch: {
    currentAgent(newVal, oldVal) {
      if (newVal !== oldVal && newVal) {
        this.agent = this.agents?.find((a: IGlobalStoreAgent) => a.agentId === newVal);
      }
    },
    agents(newVal) {
      if (newVal) {
        [this.agent] = newVal;
      }
    },
    sendRepEmailError(newVal) {
      if (newVal) {
        this.err = true;
        this.submitted = false;
        window.setTimeout(() => {
          document.getElementById('errorAlert')?.scrollIntoView();
        }, 0);
        window.setTimeout(() => {
          this.err = false;
        }, 10000);
      }
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.blueButtonWhiteText {
  background-color: #063562;
  color: #ffffff;
}

.whiteButtonBlueText {
  background-color: #ffffff;
  color: #063562;
}

.buttonCommonCss {
  text-align: center;
  font-size: 16px;
  width: 135px;
  height: 40px;
  line-height: 24px;
  border: 2px solid #063562;
  padding-top: 8px;
  display: inline-block;
  font-weight: 600;
}

.maxSizeForCloseButton {
  max-width: 360px !important;
}

.OkButton {
  margin: auto !important;
  background-color: #063562 !important;
  color: white;
  font-weight: 600;
  width: 280px;
}

:deep(label.v-label.theme--light.v-label--active) {
  font-weight: 500 !important;
  color: #000 !important;
  margin-top: -12px;
}

:deep(.v-input) {
  &.v-input--is-disabled {
    .v-input__slot:before {
      border-image: none;
    }
  }

  input {
    color: #767575;
  }
}

:deep(.v-btn__content) {
  font-weight: 500 !important;
}

:deep(.contact-black .v-field__input),
:deep(.contact-black .v-field-label),
:deep(.contact-black .v-label) {
  color: #000000 !important;
}

:deep(.contact-rep-dialog-content) {
  max-height: calc(100vh - 24px);
  max-height: calc(100dvh - 24px);
}

:deep(.contact-rep-dialog-content > .contact-rep-card),
:deep(.contact-rep-dialog-content > form > .contact-rep-card) {
  display: block !important;
  max-height: calc(100vh - 24px);
  max-height: calc(100dvh - 24px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
</style>
